'use client'

import React, { useState, useEffect } from 'react'
import { useQuery, useMutation } from '@apollo/client'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Skeleton } from '@/components/ui/skeleton'
import { GET_TAGS_FOR_SELECT } from '../../Tags/tagsQueries'
import { UPDATE_PRODUCT_TAGS } from '../productMutations'
import { GET_PRODUCT_DETAILS } from '../productQueries'

interface TagEditModalProps {
  isOpen: boolean
  onClose: () => void
  productId: number
  currentTagIds: number[]
  language: string
}

export function TagEditModal({
  isOpen,
  onClose,
  productId,
  currentTagIds,
  language,
}: TagEditModalProps) {
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([])

  const { data: tagsData, loading: tagsLoading } = useQuery(
    GET_TAGS_FOR_SELECT,
    {
      skip: !isOpen,
    }
  )

  const [updateTags, { loading: updating }] = useMutation(
    UPDATE_PRODUCT_TAGS,
    {
      refetchQueries: [
        { query: GET_PRODUCT_DETAILS, variables: { id: productId } },
      ],
      onCompleted: () => {
        onClose()
      },
    }
  )

  useEffect(() => {
    if (isOpen) {
      setSelectedTagIds(currentTagIds)
    }
  }, [isOpen, currentTagIds])

  const handleToggleTag = (tagId: number) => {
    setSelectedTagIds(prev =>
      prev.includes(tagId)
        ? prev.filter(id => id !== tagId)
        : [...prev, tagId]
    )
  }

  const handleSave = async () => {
    try {
      await updateTags({
        variables: {
          id: productId,
          tags: selectedTagIds.map(id => ({ id })),
        },
      })
    } catch (error) {
      console.error('Error updating tags:', error)
    }
  }

  const handleClose = () => {
    setSelectedTagIds(currentTagIds)
    onClose()
  }

  const getTagName = (tag: any) => {
    return (
      tag.tag_profiles.find(
        (profile: any) => profile.language.toLowerCase() === language
      )?.name || tag.tag_profiles[0]?.name
    )
  }

  const tags = tagsData?.findManyTag || []

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Edit Tags</DialogTitle>
          <DialogDescription>
            Select which tags apply to this product.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          {tagsLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex items-center space-x-3">
                  <Skeleton className="h-4 w-4" />
                  <Skeleton className="h-4 w-32" />
                </div>
              ))}
            </div>
          ) : tags.length === 0 ? (
            <p className="text-sm text-gray-500">No tags available.</p>
          ) : (
            <div className="grid grid-cols-1 gap-2 max-h-80 overflow-y-auto">
              {tags.map((tag: any) => (
                <div
                  key={tag.id}
                  className="flex items-center space-x-3 p-2 rounded hover:bg-gray-50"
                >
                  <Checkbox
                    id={`tag-${tag.id}`}
                    checked={selectedTagIds.includes(tag.id)}
                    onCheckedChange={() => handleToggleTag(tag.id)}
                  />
                  <label
                    htmlFor={`tag-${tag.id}`}
                    className="text-sm font-medium leading-none cursor-pointer"
                  >
                    {getTagName(tag)}
                  </label>
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={updating || tagsLoading}>
            {updating ? (
              <div className="flex items-center gap-2">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                Saving...
              </div>
            ) : (
              'Save'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
